/* eslint-disable react/jsx-one-expression-per-line, max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

// TODO: refactor to remove mui components
import Radio from '@zg-rentals/multibrand-ui/lib/Radio';
import CheckCircle from '@zg-rentals/multibrand-ui/lib/icons/CheckCircle';

import {
    Button,
    Flex,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';

import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';

import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

import { StyledModalSection } from 'app/shared/containers/ModalContainer/StyledComponents';

import {
    StyledActionsContainer,
    StyledBannerContainer,
    StyledContainer,
} from './StyleComponents';

const { DEACTIVATE_OPTION } = PaidInclusionConstants;

const PaidInclusionDeactivationModal = ({
    onCancelClick,
    onGotItClick,
    onDeactivate,
    expirationDate,
    autoBillEnabled,
    isDeactivationScheduled,
    errorMessage,
    isEligibleForLeadGuarantee,
}) => {
    const [deactivateOn, setDeactivateOn] = useState(autoBillEnabled ? '' : DEACTIVATE_OPTION.NOW);
    const isDeactivateNowSelected = deactivateOn === DEACTIVATE_OPTION.NOW;
    const isDeactivateEotSelected = deactivateOn === DEACTIVATE_OPTION.EOT;

    const renderLeadGuaranteeBanner = () => {
        const heading = 'Backed by our lead guarantee';
        const body = `Your listing is still eligible for our lead guarantee.
            In order to qualify for the lead guarantee again, this listing must remain off-market for 30 consecutive days.`;

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                paddingXLOnDesktop={true}
            />
        );
    };

    if (errorMessage) {
        return (
            <Spacer>
                <Text type="body">{errorMessage}</Text>
            </Spacer>
        );
    }

    if (isDeactivationScheduled) {
        return (
            <StyledContainer className="PaidInclusionDeactivationModal">
                <StyledModalSection className="PIDM-ModalSection">
                    <Flex
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                    >
                        <Spacer marginBottom="md">
                            <CheckCircle colorType="success" size={100} />
                        </Spacer>
                    </Flex>

                    <Spacer marginBottom="md">
                        <Text type="h2" data-test="deactivation-date">Your listing will deactivate on {expirationDate}.</Text>
                    </Spacer>
                    <Spacer marginBottom="md">
                        <Text type="body">
                            When your listing deactivates, it will no longer be visible to prospective renters. You may
                            activate the listing again at any time.
                        </Text>
                    </Spacer>
                </StyledModalSection>

                <StyledActionsContainer className="PIDM-ActionsContainer">
                    <Flex
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                    >
                        <Button type="primary" full={true} onClick={onGotItClick}>
                            Got it
                        </Button>
                    </Flex>
                </StyledActionsContainer>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer className="PaidInclusionDeactivationModal">
            {isEligibleForLeadGuarantee && (
                <StyledBannerContainer className="PIDM-BannerContainer">
                    <Spacer>
                        {renderLeadGuaranteeBanner()}
                    </Spacer>
                </StyledBannerContainer>
            )}

            <Spacer marginY="lg">
                <StyledModalSection className="PIDM-ModalSection">
                    <Spacer marginBottom="md">
                        <Text type="body" data-test="renew-or-expire">
                        Your listing {autoBillEnabled ? 'is scheduled to renew' : 'will expire'} on&nbsp;
                            <strong>{expirationDate}</strong>. If you deactivate this listing it will not be charged again.
                        </Text>
                    </Spacer>

                    <Spacer
                        marginBottom="md"
                        data-test="deactivate-now-spacer-row"
                        className={cx('Spacer-radio', {
                            checked: isDeactivateNowSelected,
                        })}
                    >
                        <Radio
                            data-test="deactivate-now-radio"
                            label="Deactivate listing now."
                            id={DEACTIVATE_OPTION.NOW}
                            onChange={setDeactivateOn}
                            checked={isDeactivateNowSelected}
                        />
                        {isDeactivateNowSelected && (
                            <Text type="body">Your listing will no longer be visible to prospective renters.</Text>
                        )}
                    </Spacer>

                    {autoBillEnabled && (
                        <Spacer
                            marginBottom="md"
                            data-test="deactivate-eot-spacer-row"
                            className={cx('Spacer-radio', {
                                checked: isDeactivateEotSelected,
                            })}
                        >
                            <Radio
                                data-test="deactivate-eot-radio"
                                label={`Deactivate listing on ${expirationDate}.`}
                                id={DEACTIVATE_OPTION.EOT}
                                onChange={setDeactivateOn}
                                checked={isDeactivateEotSelected}
                            />
                            {isDeactivateEotSelected && (
                                <Text type="body">
                                Your listing will remain active until&nbsp;
                                    <strong>{expirationDate}</strong>. When it expires, it will no longer be visible to
                                prospective renters.
                                </Text>
                            )}
                        </Spacer>
                    )}
                </StyledModalSection>
            </Spacer>

            <StyledActionsContainer className="PIDM-ActionsContainer">
                <Flex
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    <Spacer marginRight="md">
                        <TextButton
                            data-test="cancel-button"
                            buttonType="secondary"
                            // full={true}
                            onClick={onCancelClick}
                        >
                            Cancel
                        </TextButton>
                    </Spacer>

                    <Spacer>
                        <Button
                            data-test="deactivate-listing-button"
                            buttonType="primary"
                            disabled={!deactivateOn}
                            // full={true}
                            onClick={() => onDeactivate(deactivateOn)}
                        >
                            Confirm
                        </Button>
                    </Spacer>
                </Flex>
            </StyledActionsContainer>
        </StyledContainer>
    );
};

PaidInclusionDeactivationModal.defaultProps = {
    isDeactivationScheduled: false,
    errorMessage: '',
    isEligibleForLeadGuarantee: false,
};

PaidInclusionDeactivationModal.propTypes = {
    onCancelClick: PropTypes.func.isRequired,
    onGotItClick: PropTypes.func.isRequired,
    onDeactivate: PropTypes.func.isRequired,
    expirationDate: PropTypes.string.isRequired,
    autoBillEnabled: PropTypes.bool.isRequired,
    isDeactivationScheduled: PropTypes.bool,
    errorMessage: PropTypes.string,
    isEligibleForLeadGuarantee: PropTypes.bool,
};

export default PaidInclusionDeactivationModal;
